import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { DynamicField } from "@/modules/agents/components/DynamicField";

interface OutlookConnectionFieldsProps {
  connectionId: string | null;
  onConnectionIdChange: (id: string | null) => void;
  sharedFolderEmail: string;
  onSharedFolderEmailChange: (value: string) => void;
  sharedFolderId: string;
  onSharedFolderIdChange: (value: string) => void;
}

const stringifyDynamicInput = (input: unknown): string => String(input ?? "");

/**
 * Microsoft Outlook connection fields: the connection selector plus the shared
 * mailbox and folder pickers it unlocks.
 * @param props - OutlookConnectionFieldsProps
 * @returns Fields for selecting an Outlook connection and shared folder
 */
export function OutlookConnectionFields({
  connectionId,
  onConnectionIdChange,
  sharedFolderEmail,
  onSharedFolderEmailChange,
  sharedFolderId,
  onSharedFolderIdChange,
}: OutlookConnectionFieldsProps) {
  const sharedFolderFieldConfig = useMemo(
    () => [
      {
        type: "MANUAL" as const,
        schemaPath: ["userId"],
        input: sharedFolderEmail,
      },
    ],
    [sharedFolderEmail],
  );

  return (
    <>
      <div className="space-y-1.5">
        <Label>Outlook Connection</Label>
        <ConnectionSelector
          appId="microsoft_outlook"
          providerId="pipedream"
          connectionId={connectionId}
          onSelect={onConnectionIdChange}
        />
      </div>
      {connectionId && (
        <>
          <div className="space-y-1.5">
            <Label>Shared Folder Email</Label>
            <DynamicField
              triggerId="microsoft_outlook-new-email-in-shared-folder"
              fieldName="userId"
              connectionId={connectionId}
              value={sharedFolderEmail || undefined}
              placeholder="Select shared mailbox"
              onChange={(option) => {
                onSharedFolderEmailChange(stringifyDynamicInput(option.input));
                onSharedFolderIdChange("");
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Shared Folder ID</Label>
            <DynamicField
              triggerId="microsoft_outlook-new-email-in-shared-folder"
              fieldName="sharedFolderId"
              connectionId={connectionId}
              value={sharedFolderId || undefined}
              placeholder="Select folder"
              existingFieldConfiguration={sharedFolderFieldConfig}
              onChange={(option) =>
                onSharedFolderIdChange(stringifyDynamicInput(option.input))
              }
            />
          </div>
        </>
      )}
    </>
  );
}
